import { useState } from 'react';
import { NO_ELEMENT, rm } from 'utils';
import { useSelector, useDispatch } from 'react-redux';
import {
  getAdminProperties,
  getProductId,
  getIsFamily,
  getVariantId,
  getChannel
} from 'views/ProductPage/selector';
import { PageState } from 'models/PageState';
import { ProductSection } from './productSection';
import styles from './adminProductAttributes.scss';
import { getConfigurator } from 'views/ProductPage/configurators/selector';
import { ConfiguratorBase } from 'models/ConfiguratorBase';
import cn from 'classnames';
import { getVariantData } from 'views/ProductPage/actions';

const PriceDebug = () => {
  const isFamily = useSelector((state: PageState) => getIsFamily(state.currentPage));
  const variantId = useSelector((state: PageState) => getVariantId(state.currentPage));
  const productId = useSelector((state: PageState) => getProductId(state.currentPage));
  const saleShop = useSelector((state: PageState) => state.appShell.saleShop);
  const channel = useSelector((state: PageState) => getChannel(state));

  const LEGACY_PRICE_DEBUG_CHANNELS = ['BNO', 'PP'];
  const shouldGetOldPriceDebugUrl = LEGACY_PRICE_DEBUG_CHANNELS.includes(channel);
  const priceDebugURL = !isFamily
    ? shouldGetOldPriceDebugUrl
      ? `http://portal.bygghemma.se/pricedebug#${variantId || productId}&${saleShop}`
      : `http://portal.bygghemma.se/pricedebugv2#${variantId || productId}&${saleShop}`
    : undefined;

  if (!priceDebugURL) {
    return NO_ELEMENT;
  }

  return (
    <tr>
      <th>
        <a className={styles.link} href={priceDebugURL}>
          {rm('product_admininfo_debug')}
        </a>
      </th>
      <td />
    </tr>
  );
};

interface GetHasVariantsProps {
  configurator: ConfiguratorBase;
}

const getHasVariants = ({ configurator }: GetHasVariantsProps) => {
  return configurator && Object.keys(configurator).length > 0;
};

const ShowVariants = ({ setDisplayVariantList, showVariantList }: AttributeListProps) => {
  const configurator = useSelector((state: PageState) => getConfigurator(state.currentPage));
  const hasVariants = getHasVariants({ configurator });
  const displayVariantList = () => setDisplayVariantList(true);
  const hideVariantList = () => setDisplayVariantList(false);

  if (!hasVariants) {
    return NO_ELEMENT;
  }

  return (
    <tr onClick={showVariantList ? hideVariantList : displayVariantList}>
      <th className={styles.link}>
        {showVariantList
          ? rm('product_admininfo_hide_variants')
          : rm('product_admininfo_show_variants')}
      </th>
      <td />
    </tr>
  );
};

interface AttributeProps {
  attributes?: { [key: string]: string };
}

const getFormattedAttributes = ({ attributes }: AttributeProps) => {
  if (!attributes) {
    return [];
  }

  return Object.entries(attributes).reduce(
    (prev, [property, value]) => [...prev, { id: property, name: property, value }],
    []
  );
};

interface AttributeListProps {
  setDisplayVariantList: (value: boolean) => void;
  showVariantList: boolean;
}

const AttributeList = ({ setDisplayVariantList, showVariantList }: AttributeListProps) => {
  const adminProperties = useSelector((state: PageState) => getAdminProperties(state.currentPage));
  const attributes = getFormattedAttributes({ attributes: adminProperties });

  if (!attributes) {
    return NO_ELEMENT;
  }

  return (
    <div className={styles.productAttributes}>
      <table>
        <tbody>
          {attributes?.map(({ id, name, value }) => (
            <tr key={id}>
              <th>{name}</th>
              <td>{value}</td>
            </tr>
          ))}
          <ShowVariants
            setDisplayVariantList={setDisplayVariantList}
            showVariantList={showVariantList}
          />
          <PriceDebug />
        </tbody>
      </table>
    </div>
  );
};

interface VariantListProps {
  showVariantList: boolean;
}

const VariantList = ({ showVariantList }: VariantListProps) => {
  const configurator = useSelector((state: PageState) => getConfigurator(state.currentPage));
  const productId = useSelector((state: PageState) => getProductId(state.currentPage));
  const dispatch = useDispatch();

  if (!showVariantList) {
    return NO_ELEMENT;
  }

  return (
    <div className={styles.variantList}>
      {configurator.attributes.map(({ id, name }) => (
        <div key={id}>
          <p className={styles.name}>{name}</p>
          {Object.keys(configurator.variants).map(key => (
            <p key={key} className={styles.value}>
              {configurator.variants[key][id]}
            </p>
          ))}
        </div>
      ))}

      <div>
        <p className={styles.name}>Variant</p>
        {Object.keys(configurator.variants).map(key => {
          const loadVariant = () => dispatch(getVariantData(productId, key));

          return (
            <p key={key} className={cn(styles.link, styles.value)} onClick={loadVariant}>
              {rm('product_admininfo_go_to_variant')}
            </p>
          );
        })}
      </div>
    </div>
  );
};

interface GetHasAdminPropertiesProps {
  adminProperties?: { [key: string]: string };
}

const getHasAdminProperties = ({ adminProperties }: GetHasAdminPropertiesProps): boolean => {
  const properties = adminProperties ? Object.keys(adminProperties) : [];
  return properties.length > 0;
};

interface AdminProductAttributesProps {
  hideTitle?: boolean;
}

export const AdminProductAttributes = ({ hideTitle }: AdminProductAttributesProps) => {
  const adminProperties = useSelector((state: PageState) => getAdminProperties(state.currentPage));
  const [showVariantList, setDisplayVariantList] = useState(false);

  if (!getHasAdminProperties({ adminProperties })) {
    return NO_ELEMENT;
  }

  return (
    <ProductSection hideTitle={hideTitle} title={rm('product_admin_description_attributes')}>
      <AttributeList
        setDisplayVariantList={setDisplayVariantList}
        showVariantList={showVariantList}
      />
      <VariantList showVariantList={showVariantList} />
    </ProductSection>
  );
};
